/**
 * Shared exchange-token → activate → /home flow.
 *
 * Why this exists: both the login flow (LoginApp.tsx) and the post-signup flow
 * (SignupApp.tsx) need to exchange a one-use token for an httpOnly eb_access_token
 * cookie and then navigate to /home. Extracting here avoids copy-paste and keeps
 * the auth contract in one place.
 *
 * Why window.location.href instead of fetch: the activate endpoint is on the tenant
 * subdomain (tenant.engagebay.com) while login runs on app.engagebay.com. A cross-site
 * fetch causes Chrome's third-party cookie partitioning to block the Set-Cookie response
 * even with credentials:"include" and correct CORS headers. Navigating the browser
 * directly to the GET /activate URL makes the cookie first-party — Chrome stores it
 * without restriction. The server validates the token and issues a 302 to /home.
 */

/**
 * Navigates the browser to GET /rest/api/login/activate on the tenant subdomain.
 *
 * The server validates the exchange token, sets the eb_access_token httpOnly cookie
 * (first-party in this context), computes the post-login url_fragment from plan/coupon/route,
 * and returns a 302 to /home + fragment. No response handling needed on the frontend.
 *
 * Works for both the login and post-signup flows.
 *
 * plan/coupon/route resolution order:
 *   1. opts override (used by signup when route comes from the form dropdown)
 *   2. window.location.search (login page URL params or signup page URL params)
 *   3. omitted from query string (server treats as empty)
 *
 * @param exchangeToken  One-use Redis key from loginUserV3 or signup-user response
 * @param redirectBase   Tenant base URL, e.g. "https://acme.engagebay.com" (no trailing slash)
 * @param opts           Optional overrides for plan/coupon/route when not in URL params
 *
 * Deep-link hash preservation: if the current page URL has a hash (e.g. "#broadcasts"),
 * it is forwarded as the `deepLink` query param. The server appends it to /home only when
 * no billing fragment already applies — billing takes priority. Hashes shorter than 2
 * characters (bare "#") are ignored.
 */
export function submitActivate(
    exchangeToken: string,
    redirectBase: string | null,
    opts?: { plan?: string; coupon?: string; route?: string; deepLink?: string }
): void {
    const base = redirectBase || '';
    const urlParams = new URLSearchParams(window.location.search);
    const plan        = opts?.plan   ?? urlParams.get('plan')        ?? '';
    const coupon      = opts?.coupon ?? urlParams.get('coupon')      ?? '';
    const route       = opts?.route  ?? urlParams.get('route')       ?? '';
    const redirectURL = urlParams.get('redirectURL') ?? '';

    // Build query string for GET /activate. Only include non-empty params.
    const params = new URLSearchParams({ token: exchangeToken });
    if (plan)        params.set('plan', plan);
    if (coupon)      params.set('coupon', coupon);
    if (route)       params.set('route', route);
    if (redirectURL) params.set('redirectURL', redirectURL);

    // Preserve the pre-login deep-link hash so the server can redirect to /home#broadcasts
    // (or whatever the user was navigating to before the auth redirect). A bare "#" is skipped.
    // The server only uses this when no billing fragment applies — billing always wins.
    // opts.deepLink takes priority (used by the session-expiry return-path flow via logout.jsp
    // ?return= → LoginApp reads it and passes here); falls back to the current page hash.
    const deepLink = opts?.deepLink ?? window.location.hash;
    if (deepLink && deepLink.length > 1) params.set('deepLink', deepLink);

    window.location.href = base + '/rest/api/login/activate?' + params.toString();
}

/**
 * Persist Chrome extension auth context from login/signup URL search params, e.g.
 * /login?source=extension&ext_id=abc123
 * Survives the auth → activate redirect so the main app can send the session token
 * back to the extension (see sendTokenToExtension in EngagebayRFW.tsx).
 */
export function captureExtensionContext(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const source = urlParams.get('source');
    const extId = urlParams.get('ext_id');

    if (source === 'extension' && extId) {
        document.cookie = `ebay_chrome_ext_id=${extId};domain=.engagebay.com;path=/`;
    }
}
